import { CartNotSet } from "@busbud/busbud-errors";

import { buildSearchfromCartV2 } from "@app/helpers/search";
import { getReturnLegIfExist } from "@app/helpers/udm";
import { CartError } from "@app/modules/checkout/errors";
import type { PassengerCartInfoPassenger } from "@app/types/checkout-types";
import type { NapiDepartureCart } from "@app/types/napi-departures-types";
import type { TravellerBasicInfo } from "@app/types/napi-types";
import type { Purchaser } from "@app/types/napi-v2";
import type {
  NapiV2Cart,
  NapiV2CartItem,
  NapiV2DiscountItem,
  NapiV2TripItem
} from "@app/types/napi-v2-cart-types";
import type { PassengerQuestionCharges } from "@app/types/napi-v2-charges-types";
import type {
  NapiV2CartCharges,
  CreditRedeemCharges,
  CodeDiscount,
  ChargeItem,
  InstallmentsCharges
} from "@app/types/napi-v2-charges-types";
import type {
  NapiV2Answer,
  NapiV2Passenger
} from "@app/types/napi-v2-passengers-types";
import type { SearchInfo } from "@app/types/search-types";

import { seatSelectionHasGenders } from "./bus";

export const getCartV2TripItem = (cart_items: NapiV2CartItem[] | null) => {
  return cart_items?.find<NapiV2TripItem>(isTripItem);
};

export const getCartV2DiscountItem = (
  cart_items: NapiV2CartItem[] | null
): NapiV2DiscountItem | null => {
  return cart_items?.find<NapiV2DiscountItem>(isDiscountItem) || null;
};

export const filterTripCartItems = (cart_items: NapiV2CartItem[]) => {
  return cart_items.filter<NapiV2TripItem>(isTripItem);
};

export const isTripItem = (
  cart_item: NapiV2CartItem
): cart_item is NapiV2TripItem => {
  return cart_item.type === "trip";
};

export const isInstallmentsCharges = (
  charge_item: ChargeItem
): charge_item is InstallmentsCharges => {
  return charge_item.type === "installments";
};

export const isDiscountItem = (
  cart_item: NapiV2CartItem
): cart_item is NapiV2DiscountItem => {
  return cart_item.type === "discount";
};

export const isPassengerQuestionCharge = (
  item: ChargeItem
): item is PassengerQuestionCharges => {
  return item.type === "passenger_question";
};

const isCreditRedeemCharges = (
  charge: ChargeItem
): charge is CreditRedeemCharges => {
  return ["loyalty_credit_redeem", "cash_credit_redeem"].includes(charge.type);
};

export const filterCreditRedeemCharges = (charges: NapiV2CartCharges) => {
  return charges.items.filter<CreditRedeemCharges>(isCreditRedeemCharges);
};

export const getCartV2TripDiscount = (cart: NapiV2Cart | null) => {
  return cart?.items?.find<NapiV2DiscountItem>(isDiscountItem);
};

export const isCodeDiscount = (
  cart_charge_item: ChargeItem
): cart_charge_item is CodeDiscount => {
  return cart_charge_item.type === "code_discount";
};

export const hasRootCodeDiscount = (cart: NapiV2Cart) => {
  return cart.items.some(isDiscountItem);
};

export const getNumberOfTickets = (cart: NapiV2Cart) => {
  if (!cart) {
    return 0;
  }

  const trip = getCartV2TripItem(cart.items);
  const num_passengers = trip?.passengers.length || 0;
  const num_legs = trip?.trip_legs.length || 0;

  return num_passengers * num_legs;
};

function convertAnswersToQuestions(
  answers: NapiV2Answer[] | null
): Record<string, string | null> {
  const ret: Record<string, string | null> = {};
  if (answers !== null) {
    answers.forEach(a => {
      ret[a.question_key] = a.value;
    });
  }
  return ret;
}

export const formatPassengers = (
  passengers: NapiV2Passenger[],
  purchaser: Partial<TravellerBasicInfo & NapiV2Passenger & Purchaser> | null
) => {
  if (!passengers?.length) {
    return [];
  }

  return passengers.map((p, index) => {
    if (index === 0 && !!purchaser) {
      return formatPurchaserToPassenger(purchaser, p);
    } else {
      return {
        id: "",
        age: p.age ?? null,
        first_name: p.first_name,
        last_name: p.last_name,
        wheelchair: p.wheelchair,
        passenger_category: p.category,
        questions: convertAnswersToQuestions(p.answers),
        primary_contact: index === 0,
        email: (index === 0 && purchaser?.email) || null,
        phone_number: p.phone,
        email_opt_in: (index === 0 && purchaser?.opt_in_marketing) || false,
        address: p.address
          ? {
              line_1: p.address.address1,
              line_2: p.address.address2,
              country: p.address.country_code,
              city: p.address.city,
              postal_code: p.address.postcode,
              region: p.address.province
            }
          : null
      };
    }
  });
};

const formatPurchaserToPassenger = (
  traveller: Partial<TravellerBasicInfo & NapiV2Passenger & Purchaser>,
  passenger: NapiV2Passenger
): PassengerCartInfoPassenger => {
  return {
    id: String(traveller.id),
    age: traveller.age ?? null,
    first_name: traveller.first_name ?? "",
    last_name: traveller.last_name ?? "",
    primary_contact: true,
    email: traveller.email ?? null,
    phone_number: traveller.phone ?? null,
    email_opt_in: traveller.opt_in_marketing ?? false,
    passenger_category: traveller.category ?? "",
    address: null,
    questions: convertAnswersToQuestions(passenger.answers),
    wheelchair: traveller.wheelchair ?? false
  };
};

/**
 * Transform the cart's search info into the standard search object used
 * accross the application
 *
 * @param cart
 * @returns
 */
export function getSearch(cart: NapiV2Cart): SearchInfo {
  const trip_item = getCartV2TripItem(cart.items);

  if (!trip_item) {
    throw new Error("No trip item found in cart");
  }

  const is_roundtrip = !!getReturnLegIfExist(trip_item);
  const search_info = buildSearchfromCartV2(cart.id, cart.items);

  if (!search_info) {
    throw new Error("No search info found for cart");
  }

  return {
    ...search_info,
    // The properties below only make sense in the context of roundtrips,
    // to go back to the return leg selection page
    outbound_id: is_roundtrip ? trip_item.id : null,
    cart_id: is_roundtrip ? cart.id : null
  };
}

/**
 * Checks if the cart has a round trip or interconnection
 *
 * @param cart
 * @returns boolean
 */
export const cartHasRoundTripOrInterconnection = (cart: NapiV2Cart) => {
  const trip_item = getCartV2TripItem(cart.items);
  if (!trip_item) {
    return false;
  }
  return (
    !!getReturnLegIfExist(trip_item) ||
    trip_item.trip_legs.some(leg => (leg.segment_ids?.length ?? 0) > 1)
  );
};

export const deleteSelectedSeatsFromTripItemPassengers = (
  passengers: NapiV2TripItem["passengers"],
  seats: string[]
) => {
  const prepared_passengers = passengers.map(passenger => {
    const new_passenger = { ...passenger };

    new_passenger.selected_seats = new_passenger.selected_seats!.filter(
      selected_seat => !seats.includes(selected_seat.seat_id)
    );

    if (new_passenger.selected_seats!.length === 0 && new_passenger.answers) {
      new_passenger.answers = new_passenger.answers.filter(
        answer => answer.question_key !== "gender"
      );
    }

    return new_passenger;
  });

  return prepared_passengers;
};

export const isSeatSelectionValid = (
  trip_item: Pick<NapiV2TripItem, "segments" | "passengers">
) => {
  const has_gendered_seats = seatSelectionHasGenders(
    trip_item.segments as Pick<NapiDepartureCart, "vehicle" | "id">[]
  );

  // Currently if there is no gendered seats, we don't force user to select a seat
  if (!has_gendered_seats) {
    return true;
  }

  return (
    has_gendered_seats && haveAllPassengersGenderSelected(trip_item.passengers)
  );
};

const haveAllPassengersGenderSelected = (
  passengers: NapiV2TripItem["passengers"]
) => {
  return passengers.every(passenger =>
    passenger.answers?.find(answer => answer.question_key === "gender")
  );
};

type CartV2Assert = (cart: NapiV2Cart | undefined) => asserts cart;
export const assertCartIsSet: CartV2Assert = (
  cart
): asserts cart is NapiV2Cart => {
  if (!cart) {
    throw new CartError();
  }

  if (!cart.items || cart.items.length === 0) {
    throw new CartNotSet();
  }
};

export const getCartTripItemOperatorsPerLeg = (cart: NapiV2Cart) => {
  const trip_item = getCartV2TripItem(cart.items);
  if (!trip_item) {
    return [];
  }

  return trip_item.trip_legs.map(leg => {
    if (!leg.segment_ids) {
      return [];
    }
    return leg.segment_ids.map(
      segment_id =>
        trip_item.segments.find(segment => segment.id === segment_id)!.operator
    );
  });
};
